import React, { useCallback } from 'react';
import {
  Play,
  Bug,
  Save,
  PanelLeftClose,
  PanelLeftOpen,
  PanelBottomClose,
  PanelBottomOpen,
  RotateCcw,
  Square,
  Settings,
} from 'lucide-react';
import { useScriptEditorStore } from '../../store/scriptEditorStore';
import { cn } from '../../utils/cn';

export const ScriptEditorToolbar: React.FC = () => {
  const {
    activeTabId,
    files,
    isCompiling,
    sidebarVisible,
    bottomPanelVisible,
    toggleSidebar,
    toggleBottomPanel,
    saveFile,
    saveAllFiles,
    compile,
  } = useScriptEditorStore();

  const activeFile = activeTabId ? files[activeTabId] : null;
  const hasUnsavedFiles = Object.values(files).some(f => f.isDirty);

  const handleSave = useCallback(() => {
    if (activeTabId) {
      saveFile(activeTabId);
    }
  }, [activeTabId, saveFile]);

  const handleSaveAll = useCallback(() => {
    saveAllFiles();
  }, [saveAllFiles]);

  const handleRun = useCallback(() => {
    // TODO: Implement run functionality
    console.log('Run script');
  }, []);

  const handleDebug = useCallback(() => {
    // TODO: Implement debug functionality
    console.log('Debug script');
  }, []);

  const handleStop = useCallback(() => {
    // TODO: Implement stop functionality
    console.log('Stop execution');
  }, []);

  const handleCompile = useCallback(() => {
    compile();
  }, [compile]);

  const ToolbarButton: React.FC<{
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    disabled?: boolean;
    active?: boolean;
    variant?: 'default' | 'success' | 'warning' | 'danger';
  }> = ({ icon, label, onClick, disabled, active, variant = 'default' }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center gap-1.5 px-2 py-1 rounded text-sm transition-colors',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && variant === 'default' && 'hover:bg-zinc-700 text-zinc-300',
        !disabled && variant === 'success' && 'hover:bg-green-900/50 text-green-400',
        !disabled && variant === 'warning' && 'hover:bg-yellow-900/50 text-yellow-400',
        !disabled && variant === 'danger' && 'hover:bg-red-900/50 text-red-400',
        active && 'bg-zinc-700'
      )}
      title={label}
    >
      {icon}
    </button>
  );

  const ToolbarDivider: React.FC = () => (
    <div className="w-px h-5 bg-zinc-700 mx-1" />
  );

  return (
    <div className="flex items-center gap-1 px-2 py-1 bg-zinc-900 border-b border-zinc-800">
      {/* Run/Debug Controls */}
      <ToolbarButton
        icon={<Play className="w-4 h-4" />}
        label="Run (F5)"
        onClick={handleRun}
        disabled={!activeFile}
        variant="success"
      />
      <ToolbarButton
        icon={<Bug className="w-4 h-4" />}
        label="Debug (F9)"
        onClick={handleDebug}
        disabled={!activeFile}
        variant="warning"
      />
      <ToolbarButton
        icon={<Square className="w-4 h-4" />}
        label="Stop (Shift+F5)"
        onClick={handleStop}
        disabled={!activeFile}
        variant="danger"
      />

      <ToolbarDivider />

      {/* Save Controls */}
      <ToolbarButton
        icon={<Save className="w-4 h-4" />}
        label="Save (Ctrl+S)"
        onClick={handleSave}
        disabled={!activeFile || !activeFile.isDirty}
      />
      <button
        onClick={handleSaveAll}
        disabled={!hasUnsavedFiles}
        className={cn(
          'px-2 py-1 rounded text-xs transition-colors',
          hasUnsavedFiles
            ? 'hover:bg-zinc-700 text-zinc-300'
            : 'opacity-50 cursor-not-allowed text-zinc-500'
        )}
        title="Save All (Ctrl+Shift+S)"
      >
        Save All
      </button>

      <ToolbarDivider />

      {/* Build Controls */}
      <ToolbarButton
        icon={<RotateCcw className={cn('w-4 h-4', isCompiling && 'animate-spin')} />}
        label="Compile (Ctrl+B)"
        onClick={handleCompile}
        disabled={isCompiling}
      />

      {/* Spacer */}
      <div className="flex-1" />

      {/* Panel Toggles */}
      <ToolbarButton
        icon={sidebarVisible ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
        label={sidebarVisible ? 'Hide Sidebar' : 'Show Sidebar'}
        onClick={toggleSidebar}
        active={sidebarVisible}
      />
      <ToolbarButton
        icon={bottomPanelVisible ? <PanelBottomClose className="w-4 h-4" /> : <PanelBottomOpen className="w-4 h-4" />}
        label={bottomPanelVisible ? 'Hide Bottom Panel' : 'Show Bottom Panel'}
        onClick={toggleBottomPanel}
        active={bottomPanelVisible}
      />

      <ToolbarDivider />

      {/* Settings */}
      <ToolbarButton
        icon={<Settings className="w-4 h-4" />}
        label="Editor Settings"
        onClick={() => console.log('Open settings')}
      />
    </div>
  );
};
